/**
 * DISTRIBUTION-MAP MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TrialBanner from "../../../shared/components/TrialBanner.jsx";
import HomeButton from "../../../shared/components/HomeButton.jsx";
import { useBilling } from "../../../shared/hooks/useBilling.js";
import { useConfirm } from "../../../shared/components/ConfirmDialog.jsx";
import { listMyMaps, createMap, publishMap, deleteMap, updateMapSettings } from "../utils/mapsService.js";
import { STATE_GEOMETRY } from "../../../../../map-core/render.js";

// The fallback is the real marketing root, not a placeholder — `deploy.mjs --embed`
// uploads embed/dist/*.js to EMBED_REMOTE_DIR = /brandcommand.app, so this URL is
// live and correct even in a build with no .env.local. It used to read
// `https://YOUR-EMBED-HOST/embed.js`, which handed customers an unresolvable host
// (item 384); the sibling services in system-settings and retail-product-finder
// already fall back here.
const EMBED_SRC = import.meta.env.VITE_EMBED_SRC || "https://brandcommand.app/embed.js";

function embedSnippet(mapId) {
  return `<div data-distmap="${mapId}"></div>\n<script src="${EMBED_SRC}" async></script>`;
}

const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const TYPE_LABEL = { distributor: "Distributor", region: "Region", other: "Custom" };
const typeLabel = (t) => TYPE_LABEL[t] || "Distributor";

// Decorative US silhouette used as the map-card banner watermark — ties the
// dashboard to the product (it's a map builder). Static geometry, one color.
function UsSilhouette() {
  return (
    <svg className="us-silhouette" viewBox="0 0 960 600" aria-hidden="true">
      {STATE_GEOMETRY.codes.map((code) => (
        <path key={code} d={STATE_GEOMETRY.paths[code]} />
      ))}
    </svg>
  );
}

const PlusIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
);
const LockIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
);
const DistIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" /><path d="M9 4v14M15 6v14" /></svg>
);
const RegionIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6-7-11a7 7 0 0 1 14 0c0 5-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>
);
const OtherIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
);

// Card action icons
const sw = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
const OpenIcon = <svg viewBox="0 0 24 24" {...sw}><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" /></svg>;
const PublishIcon = <svg viewBox="0 0 24 24" {...sw}><path d="M12 18V6" /><path d="m6 11 6-6 6 6" /><path d="M5 21h14" /></svg>;
const UnpublishIcon = <svg viewBox="0 0 24 24" {...sw}><path d="M12 6v12" /><path d="m18 13-6 6-6-6" /><path d="M5 3h14" /></svg>;
const CodeIcon = <svg viewBox="0 0 24 24" {...sw}><path d="m8 6-6 6 6 6" /><path d="m16 6 6 6-6 6" /></svg>;
const CheckIcon = <svg viewBox="0 0 24 24" {...sw} strokeWidth={2.4}><path d="M20 6 9 17l-5-5" /></svg>;
const TrashIcon = <svg viewBox="0 0 24 24" {...sw}><path d="M3 6h18" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>;

// Inline styles for the create-type modal (kept local to avoid touching global CSS).
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(17,24,39,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 };
const dialogStyle = { background: "var(--surface)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 14, padding: 24, width: 470, maxWidth: "94vw", boxShadow: "var(--shadow-pop)" };
const typeCardStyle = { display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", padding: "12px 14px", border: "1px solid var(--border)", borderRadius: "var(--r-control)", background: "var(--surface)", color: "var(--text)", boxShadow: "none" };
const typeIconStyle = { width: 38, height: 38, borderRadius: 10, background: "var(--primary-soft)", color: "var(--primary)", display: "grid", placeItems: "center", flex: "0 0 auto" };
const soonBadgeStyle = { marginLeft: "auto", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--muted)", background: "var(--elevated)", padding: "3px 8px", borderRadius: "var(--r-pill)", whiteSpace: "nowrap" };

// Inline-editable map title on a card. Looks like text until hovered/focused.
function InlineTitle({ value, onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  function save() {
    const t = v.trim();
    if (!t) { setV(value); return; }       // empty -> revert
    if (t !== value) onSave(t);
  }
  return (
    <input
      className="map-title-input"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        else if (e.key === "Escape") { setV(value); e.currentTarget.blur(); }
      }}
      maxLength={120}
      aria-label="Map name"
      title="Click to rename"
    />
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const askConfirm = useConfirm();
  const { isPro, entitledRegions } = useBilling();
  const [maps, setMaps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [newName, setNew] = useState("My Map");
  const [copiedId, setCopiedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  async function refresh() {
    try { setMaps(await listMyMaps()); }
    catch (e) { setErr(e.message); }
  }
  useEffect(() => { refresh(); }, []);

  const limitReached = !isPro && maps.length >= 1;
  const noUsEntitlement = !entitledRegions.has("us");
  const canCreate = !busy && !limitReached && !noUsEntitlement;

  async function handleCreate(mapType) {
    if (!canCreate) return;
    setBusy(true); setErr(null);
    try {
      const id = await createMap({ name: newName.trim() || "My Map", mapType });
      navigate(`/maps/${id}`);
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  }

  async function togglePublish(map) {
    try { await publishMap(map.id, !map.is_published); await refresh(); }
    catch (e) { setErr(e.message); }
  }

  async function handleDelete(map) {
    const ok = await askConfirm({
      title: "Delete this map?",
      message: `Delete "${map.name}"? This can't be undone. If this map is published, its embed code will stop working immediately and the map will fail to load on every website where it's embedded.`,
      confirmLabel: "Delete map",
      danger: true,
    });
    if (!ok) return;
    try { await deleteMap(map.id); await refresh(); }
    catch (e) { setErr(e.message); }
  }

  async function renameMap(id, name) {
    setErr(null);
    try {
      await updateMapSettings(id, { name });
      setMaps((prev) => prev.map((x) => (x.id === id ? { ...x, name } : x)));
    } catch (e) { setErr(e.message); }
  }

  async function copyEmbed(map) {
    try {
      await navigator.clipboard.writeText(embedSnippet(map.id));
      setCopiedId(map.id);
      setTimeout(() => setCopiedId((c) => (c === map.id ? null : c)), 1500);
    } catch {
      setErr("Couldn't copy automatically — use the Embed code box below.");
    }
  }

  return (
    <>
      <TrialBanner />

      <HomeButton onClick={() => navigate("/")} />

      <header className="dash-head">
        <div>
          <h1>Your distribution maps</h1>
          <p className="dash-sub">Build a US distributor map, publish it, and embed it on your website.</p>
        </div>
      </header>

      {noUsEntitlement && (
        <p className="error">Your account is missing the USA region entitlement. Check your subscription.</p>
      )}
      {err && <p className="error">{err}</p>}

      <div className="maps-grid">
        {/* Create / upsell tile — always the first cell */}
        {limitReached ? (
          <div className="create-tile upsell">
            <div className="create-icon">{LockIcon}</div>
            <h3>You've used your map</h3>
            <p className="ct-sub">The Standard plan includes one map. Upgrade to Pro to build more.</p>
            <Link to="/subscribe"><button className="btn">Upgrade to Pro</button></Link>
          </div>
        ) : (
          <div
            className="create-tile"
            role="button"
            tabIndex={0}
            onClick={() => setCreateOpen(true)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setCreateOpen(true); } }}
            style={{ cursor: "pointer" }}
          >
            <div className="create-icon">{PlusIcon}</div>
            <h3>Create a map</h3>
            <p className="ct-sub">Distributor, region, and more.</p>
          </div>
        )}

        {/* Existing maps */}
        {maps.map((m) => (
          <div key={m.id} className="map-card">
            <Link to={`/maps/${m.id}`} className="map-card-banner" aria-label={`Open ${m.name}`}>
              <UsSilhouette />
              <span className="type-chip">{typeLabel(m.map_type)}</span>
              <span className={`status-badge ${m.is_published ? "pub" : "draft"}`}>
                <span className="dot" />{m.is_published ? "Published" : "Draft"}
              </span>
            </Link>
            <div className="map-card-body">
              <InlineTitle value={m.name} onSave={(name) => renameMap(m.id, name)} />
              <div className="meta">
                {m.is_published
                  ? `Published · ${fmtDate(m.published_at || m.updated_at)}`
                  : `Updated · ${fmtDate(m.updated_at)}`}
              </div>
              <div className="actions">
                <Link to={`/maps/${m.id}`}><button className="btn">{OpenIcon}Open</button></Link>
                <button
                  onClick={() => togglePublish(m)}
                  className="secondary pub-btn"
                  title={m.is_published
                    ? "Take this map offline — it stops loading on sites where it's embedded, until you publish again."
                    : "Make this map live so it loads on any site where you've pasted its embed code."}
                >
                  {m.is_published ? UnpublishIcon : PublishIcon}{m.is_published ? "Unpublish" : "Publish"}
                </button>
                {m.is_published && (
                  <button
                    onClick={() => copyEmbed(m)}
                    className={`secondary copy-btn${copiedId === m.id ? " copied" : ""}`}
                    aria-live="polite"
                    title="Copy the embed code, then paste it into your website's HTML where you want the map to appear."
                  >
                    {copiedId === m.id ? <>{CheckIcon}Copied!</> : <>{CodeIcon}Copy embed</>}
                  </button>
                )}
                <button onClick={() => handleDelete(m)} className="btn-del" aria-label="Delete map" title="Delete">
                  {TrashIcon}
                </button>
              </div>
              {m.is_published && (
                <details>
                  <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                    Embed code
                  </summary>
                  <code className="embed-snippet">{embedSnippet(m.id)}</code>
                </details>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create-a-map type picker */}
      {createOpen && (
        <div role="dialog" aria-modal="true" aria-label="Create a map" style={overlayStyle} onClick={() => !busy && setCreateOpen(false)}>
          <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Create a map</h2>
              <button className="link" aria-label="Close" onClick={() => setCreateOpen(false)} style={{ fontSize: 20 }}>×</button>
            </div>

            <label style={{ display: "block", marginTop: 14, fontSize: 13 }}>
              Map name
              <input
                value={newName}
                onChange={(e) => setNew(e.target.value)}
                placeholder="My distribution map"
                aria-label="New map name"
                style={{ width: "100%", marginTop: 4 }}
                onKeyDown={(e) => { if (e.key === "Enter" && canCreate) handleCreate("distributor"); }}
              />
            </label>

            <p className="muted" style={{ margin: "16px 0 8px", fontSize: 13, fontWeight: 600, color: "var(--heading)" }}>What kind of map?</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button type="button" onClick={() => handleCreate("distributor")} disabled={!canCreate} style={typeCardStyle}>
                <span style={typeIconStyle}>{DistIcon}</span>
                <span>
                  <span style={{ display: "block", fontWeight: 700 }}>Distributor Map</span>
                  <span className="muted" style={{ fontSize: 12 }}>Color US states by the distributor that covers them.</span>
                </span>
                <span style={{ marginLeft: "auto", color: "var(--muted)", fontSize: 18 }} aria-hidden="true">›</span>
              </button>

              <button type="button" onClick={() => handleCreate("region")} disabled={!canCreate} style={typeCardStyle}>
                <span style={typeIconStyle}>{RegionIcon}</span>
                <span>
                  <span style={{ display: "block", fontWeight: 700 }}>Region Map</span>
                  <span className="muted" style={{ fontSize: 12 }}>Color US states by their sales region.</span>
                </span>
                <span style={{ marginLeft: "auto", color: "var(--muted)", fontSize: 18 }} aria-hidden="true">›</span>
              </button>

              <button type="button" onClick={() => handleCreate("other")} disabled={!canCreate} style={typeCardStyle}>
                <span style={typeIconStyle}>{OtherIcon}</span>
                <span>
                  <span style={{ display: "block", fontWeight: 700 }}>Other Map</span>
                  <span className="muted" style={{ fontSize: 12 }}>Add your own legend items and paint states by hand.</span>
                </span>
                <span style={{ marginLeft: "auto", color: "var(--muted)", fontSize: 18 }} aria-hidden="true">›</span>
              </button>
            </div>

            {!canCreate && !limitReached && (
              <p className="error" style={{ marginTop: 12 }}>Your account can't create a map right now.</p>
            )}
            {busy && <p className="muted" style={{ marginTop: 12 }}>Creating…</p>}
          </div>
        </div>
      )}
    </>
  );
}
